import { tw } from "@careinfo/tailwind";
import { ColumnDef } from "@tanstack/react-table";
import APIS from "../../constant/Endpoint";
import { classNames } from "../../utils/classNames";
import DataTable from "../Reusable/Datatable";
import TableActionButton from "../Reusable/TableActionButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import NACell from "../Reusable/Datatable/NACell";
import { usePDFPrintHook } from "@careinfo/layout";
import { toast } from "react-toastify";
import { AccessControl } from "@careinfo/permission-required";

const SalesReturn = () => {
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [searchParams, setSearchParms] = useSearchParams();
  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const navigate = useNavigate();
  const handleRowClick = (row: any) =>{
    navigate(`edit/${row.original.id}`)
  }

  const handleIndividualSelect = (value: string) => {
    const clonedParams = new URLSearchParams(searchParams);
    const selectedItems = clonedParams.get("selectedItems")?.split(",");
    if (!selectedItems || !selectedItems === undefined) {
      clonedParams.set("selectedItems", value);
      setSearchParms(clonedParams);
      return;
    }
    const itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        clonedParams.set("selectedItems", newSelectedItems);
      } else {
        clonedParams.delete("selectedItems");
      }
      clonedParams.set("selectAllItems", "0");
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      clonedParams.set("selectedItems", newSelectedItems);
    }
    setSearchParms(clonedParams);
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const clonedParams = new URLSearchParams(searchParams);
    const isSelected = clonedParams.get("selectAllItems");
    if (isSelected === "1") {
      clonedParams.set("selectAllItems", "0");
      clonedParams.delete("selectedItems");
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      clonedParams.set("selectAllItems", "1");
      clonedParams.set("selectedItems", selectedItems);
    }
    setSearchParms(clonedParams);
  };

  const columns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => {
            handleIndividualSelect(row?.original?.id);
          }}
          className={tw`cursor-pointer`}
          onClick={(e)=>{e.stopPropagation()}}
        />
      ),
    },
    {
      accessorKey: "voucher_date",
      header: "Date",
      cell: ({ row, getValue }: any) => (
        <p className={tw`whitespace-nowrap`}>{getValue()}</p>
      ),
    },
    {
      accessorKey: "miti",
      header: "Miti",
      cell: ({ row, getValue }: any) => (
        <p className={tw`whitespace-nowrap`}>{getValue()}</p>
      ),
    },
    {
      accessorKey: "seq_no",
      header: "Bill No.",
      cell: NACell,
    },
    {
      accessorKey: "party.full_name",
      header: "Customer",
      cell: ({ row, getValue }: any) => (
        <p className={tw`whitespace-nowrap`}>{getValue()}</p>
      ),
    },
    {
      accessorKey: "total.items_total",
      header: "Items Value",
      cell: ({ row, getValue }) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </p>
      ),
    },
    {
      accessorKey: "total.total_discount",
      header: "Discount",
      cell: ({ row, getValue }) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </p>
      ),
    },
    {
      accessorKey: "total.taxable_amount",
      header: "Taxable Amount",
      cell: ({ row, getValue }) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </p>
      ),
    },
    {
      accessorKey: "total.non_taxable_amount",
      header: "Exempted Amount",
      cell: ({ row, getValue }) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </p>
      ),
    },
    {
      accessorKey: "total.tax_amount",
      header: "VAT Amount",
      cell: ({ row, getValue }) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? 0}
        </p>
      ),
    },

    {
      accessorKey: "total.total_amount",
      header: "Amount",
      cell: ({ row, getValue }: any) => (
        <p>
          {Number(getValue()).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </p>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row, getValue }) => (
        <p
          className={classNames(
            getValue() === "Draft" ? tw`bg-yellow-400` : tw`bg-[#32CD32]`,
            tw`px-2 py-1 text-center rounded-md`
          )}
        >
          {getValue()}
        </p>
      ),
    },
    // {
    //   accessorKey: "id",
    //   header: "Action",
    //   cell: ({ row, getValue }: any) => {
    //     return (
    //       <div className={tw`flex gap-x-4 items-center`}>
    //         <AccessControl allowedPermissions={["can_edit_sales_return"]}>
    //           <TableActionButton editPath={`edit/${getValue()}`} />
    //         </AccessControl>
    //       </div>
    //     );
    //   },
    // },
  ];

  const handlePrint = async () => {
    const selectedOrder = searchParams.get("selectedItems")?.split(",");
    if (!selectedOrder?.length) {
      toast.error("Please select at least one first...");
      return;
    }
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: selectedOrder,
      type: "Sales Return",
    };
    try {
      const res = await downloadPrint(url, formData);
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DataTable
      columns={columns}
      queryKey="sales-return"
      url={APIS.SALES_PURCHASE_RETURN}
      tableName="sales-purchase-return"
      onRowClick={handleRowClick}
      addNew={{
        path: "/accounting/sales-return/create",
        label: "Make Entry",
      }}
      params={{
        type: "Sales Return",
      }}
      actionTools={
        <>
          <div className={tw`flex  w-full items-center`}>
            <AccessControl allowedPermissions={["can_print_sales_return"]}>
              <button
                type="button"
                onClick={() => handlePrint()}
                disabled={isPrintLoading || false}
                className={tw`px-6 rounded-md py-2 bg-primary text-white focus:outline-none`}
              >
                Print
              </button>
            </AccessControl>
          </div>
        </>
      }
    />
  );
};

export default SalesReturn;

